import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://4jbhzumjk7.execute-api.us-east-2.amazonaws.com/prod';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// ─── Types ────────────────────────────────────────────────────────────────────
// Reuse SPX types — the signal structure is identical

export interface IndexCandidate {
  rank_enhanced: number;
  rank_balanced: number;
  sharpe_rank: number;
  expiration: string;
  strike: number;
  credit: number;
  credit_per_day: number;
  dte: number;
  points_from_atm: number;
  iv_pct: number;
  delta: number;
  vega: number;
  theta: number;
  prob_profit_pct: number;
  delta_bucket: string;
  sharpe_annualized: number;
  score_enhanced: number;
  score_balanced: number;
}

export interface IndexBucketBest {
  expiration: string;
  strike: number;
  dte: number;
  credit: number;
  delta: number;
  points_from_atm: number;
  prob_profit_pct: number;
  sharpe_annualized: number;
  score_enhanced: number;
  credit_per_day: number;
  delta_bucket: string;
}

export interface IndexModelBestTrade {
  expiration: string;
  strike: number;
  dte: number;
  credit: number;
  delta: number;
  theta: number;
  vega: number;
  iv_pct: number;
  points_from_atm: number;
  prob_profit_pct: number;
  sharpe_annualized: number;
  score_enhanced: number;
  score_balanced: number;
  credit_per_day: number;
  delta_bucket: string;
}

export interface IndexDteBucket {
  label: string;
  count: number;
  best?: IndexBucketBest;
  candidates: IndexCandidate[];
}

export interface IndexClaudeTopPick {
  rank: number;
  expiration: string;
  strike: number;
  dte: number;
  credit: number;
  reasoning: string;
}

export interface IndexAiRankedCandidate {
  rank: number;
  trade_number: number;
  strike: number;
  expiration: string;
  score_enhanced: number;
  our_rank: number;
  reasoning: string;
}

export interface IndexAiBestTrade {
  expiration: string;
  strike: number;
  dte: number;
  credit: number;
  credit_per_day?: number;
  delta: number;
  delta_bucket?: string;
  points_from_atm?: number;
  iv_pct?: number;
  prob_profit_pct: number;
  sharpe_annualized: number;
  score_enhanced: number;
  reasoning: string;
}

export interface IndexClaudeAnalysis {
  s3_key: string | null;
  best_trade?: IndexAiBestTrade;
  summary: string;
  market_context: string;
  risk_warnings: string[];
  top_picks: IndexClaudeTopPick[];
  ranked_candidates?: IndexAiRankedCandidate[];
  model: string;
  analyzed_at: string;
  usage?: {
    input_tokens: number;
    output_tokens: number;
  };
}

export interface IndexChatGptAnalysis {
  best_trade?: IndexAiBestTrade;
  summary: string;
  market_context: string;
  risk_warnings: string[];
  top_picks: IndexClaudeTopPick[];
  ranked_candidates?: IndexAiRankedCandidate[];
  model: string;
  analyzed_at: string;
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

export interface IndexSkew {
  skew_ratio: number;
  skew_regime: 'normal' | 'cautious' | 'elevated' | 'extreme';
  skew_penalty: number;
  expirations_measured: number;
  skew_by_expiration?: Record<string, {
    put_iv: number;
    call_iv: number;
    put_delta: number;
    call_delta: number;
    put_strike: number;
    call_strike: number;
    skew_ratio: number;
  }>;
}

export interface IndexSignal {
  symbol_date: string;
  date: string;
  symbol: string;
  snapshot_time: string;
  strategy: string;
  spot_price: number;
  capital: number;
  total_candidates: number;
  generated_at: string;
  model_best_trade?: IndexModelBestTrade;
  claude_analysis?: IndexClaudeAnalysis;
  chatgpt_analysis?: IndexChatGptAnalysis;
  dte_buckets: Record<string, IndexDteBucket>;
  skew?: IndexSkew;
}

// ─── Audit Types ──────────────────────────────────────────────────────────────

export type IndexAuditStatus = 'success' | 'failure' | 'skip' | 'warning';

export interface IndexAuditEvent {
  run_date: string;
  event_id: string;
  run_id: string;
  snapshot_time: string;
  symbol: string;
  sequence: number;
  event_type: string;
  step: string;
  status: IndexAuditStatus;
  summary: string | null;
  detail: Record<string, any> | null;
  duration_ms: number | null;
  timestamp: string;
}

export interface IndexAuditStepSummary {
  step: string;
  status: IndexAuditStatus;
  event_type: string;
  duration_ms: number | null;
}

export interface IndexAuditRunSummary {
  runId: string;
  snapshotTime: string;
  symbol: string;
  startedAt: string | null;
  completedAt: string | null;
  totalEvents: number;
  totalDurationMs: number | null;
  steps: IndexAuditStepSummary[];
  errors: number;
  status: 'success' | 'error';
}

// ─── Service ──────────────────────────────────────────────────────────────────

const authHeaders = (token: string) => ({
  headers: { Authorization: `Bearer ${token}` },
});

export const indexService = {
  async getLatest(token: string, symbol: string = 'SPY'): Promise<{ signals: IndexSignal[]; count: number }> {
    const { data } = await api.get(`/indices/signals?symbol=${symbol}`, authHeaders(token));
    return data;
  },

  async getByDate(token: string, date: string, symbol: string = 'SPY'): Promise<{ signals: IndexSignal[]; count: number }> {
    const { data } = await api.get(`/indices/signals?date=${date}&symbol=${symbol}`, authHeaders(token));
    return data;
  },

  async getHistory(token: string, startDate: string, endDate: string, symbol: string = 'SPY'): Promise<{ signals: IndexSignal[]; count: number }> {
    const { data } = await api.get(
      `/indices/signals?history=true&startDate=${startDate}&endDate=${endDate}&symbol=${symbol}`,
      authHeaders(token)
    );
    return data;
  },

  async getExcelUrl(token: string, date?: string, symbol: string = 'SPY'): Promise<string> {
    const dateParam = date ? `&date=${date}` : '';
    const { data } = await api.get(`/indices/signals?excel=true${dateParam}&symbol=${symbol}`, authHeaders(token));
    return data.excelUrl;
  },

  // ─── Audit ──────────────────────────────────────────────────────────────────

  async getAuditRuns(token: string, date?: string): Promise<{ date: string; runs: IndexAuditRunSummary[]; events: IndexAuditEvent[] }> {
    const dateParam = date ? `?date=${date}` : '';
    const { data } = await api.get(`/indices/audit${dateParam}`, authHeaders(token));
    return data;
  },

  async getAuditRunEvents(token: string, runId: string): Promise<{ events: IndexAuditEvent[] }> {
    const { data } = await api.get(`/indices/audit?runId=${encodeURIComponent(runId)}`, authHeaders(token));
    return data;
  },
};
